import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

/**
 * Hook to check if the current user has access to Collector features.
 * Returns true if:
 * - User's organization has plan_type = 'collector', OR
 * - User's profile has collector_enabled = true (via addon purchase)
 */
export const useCollectorAccess = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['collector-access', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const { data, error } = await supabase.rpc('has_collector_access');

      if (error) {
        console.error('Error checking collector access:', error);
        return false;
      }

      return data as boolean;
    },
    enabled: !!user?.id,
    staleTime: 30000, // Cache for 30 seconds
  });
};

/**
 * Hook to get both plan type and collector access status
 */
export const useCollectorStatus = () => {
  const { user } = useAuth();
  const { data: hasAccess, isLoading: accessLoading } = useCollectorAccess();

  const { data: planType, isLoading: planLoading } = useQuery({
    queryKey: ['plan-type-check', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase.rpc('get_user_plan_type');

      if (error) {
        console.error('Error fetching plan type:', error);
        return null;
      }

      return data as 'zoo' | 'sanctuary' | 'collector' | null;
    },
    enabled: !!user?.id,
    staleTime: 30000,
  });

  return {
    hasCollectorAccess: hasAccess ?? false,
    isCollectorOnly: planType === 'collector',
    isZooWithAddon: planType !== 'collector' && hasAccess,
    isLoading: accessLoading || planLoading,
  };
};
